import React, { useEffect, useState } from 'react'
import styles from './styles.module.scss'
import api from '../../../helpers/apiRequester'
import { Loading, Report } from 'notiflix'
import { useAuthUserData } from '../../../authUserProvider'

const BASE_URL =
    process.env.REACT_APP_API_URL == 'http://localhost:8080/api'
        ? 'http://localhost:8080'
        : 'https://case-delivery.sentinellegal.co.uk'

interface BatchReport {
    batch_id: string
    batch_name: string
    created_at: string
    client_count: number
}

interface SearchResult {
    id: number
    firstname: string
    lastname: string
    dob: string
    email: string
    phone: string
    created_at: string
    introducer: string
    short_link: string
    confirmed_lender: string
    batch_id: string
    open_banking_link: string
}

interface DownloadModalData {
    isOpen: boolean
    downloadUrl: string
    reportName: string
}

export const ILManagementView = () => {
    const authUserData = useAuthUserData()
    const [loading, setLoading] = useState(false)
    const [authLoading, setAuthLoading] = useState(true)
    const [authError, setAuthError] = useState(false)

    // Download Report State
    const [selectedBatch, setSelectedBatch] = useState<string>('')
    const [batchReports, setBatchReports] = useState<BatchReport[]>([])
    const [downloadLoading, setDownloadLoading] = useState(false)
    const [downloadModal, setDownloadModal] = useState<DownloadModalData>({
        isOpen: false,
        downloadUrl: '',
        reportName: '',
    })

    // Search Clients State
    const [searchQuery, setSearchQuery] = useState('')
    const [searchResults, setSearchResults] = useState<SearchResult[]>([])
    const [searchLoading, setSearchLoading] = useState(false)
    const [hasSearched, setHasSearched] = useState(false)

    // Authentication check on component mount
    useEffect(() => {
        const checkAuthentication = async () => {
            try {
                setAuthLoading(true)
                setAuthError(false)

                const response = await api.post('/il-management-auth-check')

                if (response.data === 'SUCCESS') {
                    setAuthLoading(false)
                    // Load batch reports on successful auth
                    loadBatchReports()
                } else {
                    setAuthError(true)
                    setAuthLoading(false)
                }
            } catch (error) {
                console.error('Authentication check failed:', error)
                setAuthError(true)
                setAuthLoading(false)

                Report.failure(
                    'Authentication Required',
                    'Your session has expired. Please log in again to continue.',
                    'Okay'
                )
            }
        }

        checkAuthentication()
    }, [])

    const loadBatchReports = async () => {
        try {
            const response = await api.post('/il-batch-reports')
            if (response.data && response.data.success) {
                setBatchReports(response.data.reports || [])
            }
        } catch (error) {
            console.error('Error loading batch reports:', error)
            Report.failure('Error', 'Failed to load batch reports', 'Okay')
        }
    }

    const handleDownloadReport = async () => {
        if (!selectedBatch && selectedBatch !== 'all') {
            Report.failure('Error', 'Please select a batch report or "All Clients"', 'Okay')
            return
        }

        setDownloadLoading(true)

        try {
            const response = await api.post('/il-download-report', {
                batchId: selectedBatch,
            })

            if (response.data && response.data.success) {
                const reportName =
                    selectedBatch === 'all'
                        ? 'All IL Clients Report'
                        : batchReports.find((b) => b.batch_id === selectedBatch)?.batch_name || 'IL Clients Report'

                setDownloadModal({
                    isOpen: true,
                    downloadUrl: response.data.downloadUrl,
                    reportName: reportName,
                })
            } else {
                Report.failure('Error', response.data?.message || 'Failed to generate report', 'Okay')
            }
        } catch (error: any) {
            console.error('Error downloading report:', error)
            Report.failure(
                'Error',
                error.response?.data?.message || error.message || 'An unexpected error occurred',
                'Okay'
            )
        } finally {
            setDownloadLoading(false)
        }
    }

    const handleSearchClients = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!searchQuery.trim()) {
            Report.failure('Error', 'Please enter a search term', 'Okay')
            return
        }

        setSearchLoading(true)
        setHasSearched(true)

        try {
            const response = await api.post('/il-search-clients', {
                searchTerm: searchQuery.trim(),
            })

            if (response.data && response.data.success) {
                setSearchResults(response.data.results || [])
                if (response.data.results.length === 0) {
                    Report.info('No Results', 'No clients found matching your search criteria', 'Okay')
                }
            } else {
                Report.failure('Error', response.data?.message || 'Search failed', 'Okay')
            }
        } catch (error: any) {
            console.error('Error searching clients:', error)
            Report.failure(
                'Error',
                error.response?.data?.message || error.message || 'An unexpected error occurred',
                'Okay'
            )
        } finally {
            setSearchLoading(false)
        }
    }

    const handleClientClick = (clientId: string) => {
        // Navigate to IL client details page
        window.location.href = `/il-client/${clientId}`
    }

    const closeDownloadModal = () => {
        setDownloadModal({
            isOpen: false,
            downloadUrl: '',
            reportName: '',
        })
    }

    return (
        <div className={styles.ilManagementView}>
            {/* Authentication Loading */}
            {authLoading && (
                <div className={styles.loadingOverlay}>
                    <div className={styles.loadingContent}>
                        <div className={styles.spinner}></div>
                        <p>Verifying authentication...</p>
                    </div>
                </div>
            )}

            {/* Authentication Error */}
            {authError && (
                <div className={styles.authError}>
                    <div className={styles.authErrorContent}>
                        <h3>Authentication Required</h3>
                        <p>Your session has expired. Please log in again to continue.</p>
                        <button onClick={() => (window.location.href = '/login')} className={styles.authButton}>
                            Go to Login
                        </button>
                    </div>
                </div>
            )}

            {/* Loading Overlay */}
            {loading && (
                <div className={styles.loadingOverlay}>
                    <div className={styles.loadingContent}>
                        <div className={styles.loadingSpinner}></div>
                        <p className={styles.loadingText}>Loading...</p>
                    </div>
                </div>
            )}

            <div className={styles.container}>
                <div className={styles.header}>
                    <h1 className={styles.title}>IL Management</h1>
                    <p className={styles.subtitle}>
                        Download batch reports and search for existing IL clients in the system.
                    </p>
                </div>

                <div className={styles.content}>
                    {/* Download Report Section */}
                    <div className={styles.card}>
                        <div className={styles.cardHeader}>
                            <h2 className={styles.cardTitle}>Download Reports</h2>
                            <p className={styles.cardDescription}>
                                Select a batch report to download or download all clients data.
                            </p>
                        </div>

                        <div className={styles.formGroup}>
                            <label className={styles.label}>Select Report *</label>
                            <select
                                value={selectedBatch}
                                onChange={(e) => setSelectedBatch(e.target.value)}
                                className={styles.select}
                            >
                                <option value="">Choose a report...</option>
                                <option value="all">All Clients</option>
                                {batchReports.map((report) => (
                                    <option key={report.batch_id} value={report.batch_id}>
                                        {report.batch_name} ({report.client_count} clients) -{' '}
                                        {new Date(report.created_at).toLocaleDateString('en-GB')}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className={styles.formActions}>
                            <button
                                onClick={handleDownloadReport}
                                disabled={downloadLoading || !selectedBatch}
                                className={styles.downloadButton}
                            >
                                {downloadLoading ? 'Generating Report...' : 'Download Report'}
                            </button>
                        </div>
                    </div>

                    {/* Search Clients Section */}
                    <div className={styles.card}>
                        <div className={styles.cardHeader}>
                            <h2 className={styles.cardTitle}>Search Clients</h2>
                            <p className={styles.cardDescription}>
                                Search for existing IL clients by name, email address, or phone number.
                            </p>
                        </div>

                        <form onSubmit={handleSearchClients} className={styles.searchForm}>
                            <div className={styles.formGroup}>
                                <label className={styles.label}>Search Query *</label>
                                <div className={styles.inputContainer}>
                                    <input
                                        type="text"
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className={styles.input}
                                        placeholder="Enter name, email, or phone number..."
                                        disabled={searchLoading}
                                    />
                                    {searchLoading && (
                                        <div className={styles.searchLoader}>
                                            <div className={styles.spinner}></div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className={styles.formActions}>
                                <button
                                    type="submit"
                                    disabled={searchLoading || !searchQuery.trim()}
                                    className={styles.searchButton}
                                >
                                    {searchLoading ? 'Searching...' : 'Search'}
                                </button>
                            </div>
                        </form>

                        {/* Search Results */}
                        {hasSearched && !searchLoading && (
                            <div className={styles.searchResults}>
                                <h3 className={styles.resultsTitle}>Search Results ({searchResults.length} found)</h3>

                                {searchResults.length > 0 ? (
                                    <div className={styles.resultsTable}>
                                        <div className={styles.tableHeader}>
                                            <div className={styles.tableCell}>Name</div>
                                            <div className={styles.tableCell}>Email</div>
                                            <div className={styles.tableCell}>Introducer</div>
                                            <div className={styles.tableCell}>Action</div>
                                        </div>

                                        {searchResults.map((client) => (
                                            <div key={client.id} className={styles.tableRow}>
                                                <div className={styles.tableCell}>
                                                    {client.firstname} {client.lastname}
                                                </div>
                                                <div className={styles.tableCell}>{client.email}</div>
                                                <div className={styles.tableCell}>{client.introducer}</div>
                                                <div className={styles.tableCell}>
                                                    <button
                                                        onClick={() => handleClientClick(client.id.toString())}
                                                        className={styles.viewClientButton}
                                                    >
                                                        View Client
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className={styles.noResults}>
                                        <p>No clients found matching your search criteria.</p>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Download Modal */}
            {downloadModal.isOpen && (
                <div className={styles.modalOverlay} onClick={closeDownloadModal}>
                    <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                        <button onClick={closeDownloadModal} className={styles.closeButton}>
                            ×
                        </button>
                        <div className={styles.modalHeader}>
                            <h3>Report Ready for Download</h3>
                        </div>

                        <div className={styles.modalBody}>
                            <p className={styles.reportDescription}>
                                Your <strong>{downloadModal.reportName}</strong> has been generated successfully and is
                                ready for download.
                            </p>

                            <div className={styles.downloadLinkContainer}>
                                <a
                                    href={downloadModal.downloadUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={styles.downloadLink}
                                >
                                    Download {downloadModal.reportName}
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
